import { useState, useEffect } from 'react';
import axios from 'axios';
import { LintBadge } from './LintBadge';
import ClaimAudit from './ClaimAudit';
import { Sparkles, Send, Eye, Pencil, Check, Loader2, X, FileText, Mail, MailOpen } from 'lucide-react';
import { EmailImagePanel } from './EmailImagePanel';
import NewsletterComposer from './NewsletterComposer';
// Relocated from the retired Campaign Planner page (2026-07-27 consolidation).
import { TemplateGallery, ArchiveBrowser } from './CampaignPlanner';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

function SectionEditor({ section, index, onChange, onImageChange }) {
  const [editing, setEditing] = useState(false);

  return (
    <div className="bg-hg-bg border border-hg-border rounded-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-sans font-medium uppercase tracking-wider text-hg-green">{section.title}</h4>
        <button
          data-testid={`nl-section-edit-${index}`}
          onClick={() => setEditing(!editing)}
          className="text-hg-text-dim hover:text-hg-green transition-colors"
        >
          <Pencil size={12} strokeWidth={1.5} />
        </button>
      </div>
      {editing ? (
        <textarea
          data-testid={`nl-section-textarea-${index}`}
          value={section.copy}
          onChange={(e) => onChange(index, e.target.value)}
          className="w-full bg-hg-surface border border-hg-border rounded-sm p-3 text-sm text-hg-text font-sans resize-none focus:outline-none focus:ring-1 focus:ring-hg-green"
          rows={5}
        />
      ) : (
        <p className="text-sm text-hg-text/90 font-sans leading-relaxed whitespace-pre-line">{section.copy}</p>
      )}
      {section.lint && (
        <div data-testid={`nl-section-lint-${index}`}>
          <LintBadge lint={section.lint} testid={`nl-lint-badge-${index}`} />
        </div>
      )}
      <ClaimAudit
        text={section.copy}
        onTextChange={(newText) => onChange(index, newText)}
        testid={`nl-section-${index}-audit`}
      />
      <EmailImagePanel
        testIdPrefix={`nl-section-image-${index}`}
        imageData={section.image}
        onImageSet={(data) => onImageChange(index, data)}
        context="newsletter"
        promptContext={section.title || ''}
        label={`Section Image (optional)`}
        size="compact"
      />
    </div>
  );
}

function PreviewModal({ draft, onClose }) {
  if (!draft) return null;
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-hg-surface border border-hg-border rounded-sm max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="font-display text-2xl text-hg-text tracking-wider">{draft.subject}</h2>
            <p className="text-sm text-hg-text-dim font-sans mt-1">{draft.preview}</p>
          </div>
          <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={18} /></button>
        </div>
        <div className="space-y-6">
          {draft.sections?.map((s, i) => (
            <div key={i}>
              <h3 className="font-display text-sm font-semibold text-hg-green mb-2 tracking-wider">{s.title}</h3>
              <p className="text-sm text-hg-text/90 font-sans leading-relaxed whitespace-pre-line">{s.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FormattedPreviewModal({ draft, onClose }) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.post(`${API}/templates/preview`, {
          style: 'newsletter',
          subject: draft.subject,
          intro: draft.preview,
          sections: draft.sections || [],
          hero_image: draft.hero_image || null,
        });
        if (!cancelled) setHtml(res.data.html);
      } catch (e) { console.error(e); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [draft]);

  if (!draft) return null;

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="nl-formatted-preview">
      <div className="bg-white rounded-sm w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-hg-surface flex items-center justify-between px-4 py-3 border-b border-hg-border">
          <span className="text-sm font-sans text-hg-text">HG Newsletter — Formatted Preview</span>
          <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={18} /></button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-[600px]"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
        ) : (
          <iframe srcDoc={html} title="Formatted Preview" className="w-full flex-1 min-h-[600px] border-0" sandbox="allow-same-origin" />
        )}
      </div>
    </div>
  );
}

function HistoryTab() {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDraft, setViewDraft] = useState(null);
  const [statsById, setStatsById] = useState({}); // campaign_id -> stats

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [hist, analytics] = await Promise.all([
          axios.get(`${API}/newsletter/history`),
          axios.get(`${API}/mailerlite/analytics?status=all&limit=50`).catch(() => ({ data: { campaigns: [] } })),
        ]);
        if (cancelled) return;
        setNewsletters(hist.data.newsletters);
        const map = {};
        for (const c of (analytics.data?.campaigns || [])) {
          map[c.campaign_id] = c.stats;
        }
        setStatsById(map);
      } catch (e) {
        console.error('Failed to load newsletter history', e);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-3">
      {loading && <div className="text-center py-8 text-hg-text-dim text-sm">Loading...</div>}
      {!loading && newsletters.length === 0 && (
        <div className="text-center py-12 text-hg-text-dim text-sm">No newsletters yet. Generate your first draft above.</div>
      )}
      {newsletters.map((nl, i) => {
        const campId = nl.mailerlite_campaign_id || nl.beehiiv_post_id;
        const stats = campId ? statsById[campId] : null;
        const opens = stats?.opens_count || 0;
        const openRate = stats?.open_rate || 0;
        return (
          <div key={nl.id || i} data-testid={`newsletter-history-row-${i}`} className="bg-hg-surface border border-hg-border rounded-sm p-4 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-hg-text-dim font-sans">
                  {new Date(nl.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-sm font-sans ${
                  nl.status === 'pushed' ? 'bg-hg-green/10 text-hg-green' : 'bg-hg-border text-hg-text-dim'
                }`}>
                  {nl.status}
                </span>
                {stats && opens > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-sm font-sans bg-hg-green/10 text-hg-green flex items-center gap-1" data-testid={`newsletter-stats-${i}`}>
                    <MailOpen size={10} /> {opens} opens · {Math.round(openRate * 100) / 100}%
                  </span>
                )}
              </div>
              <p className="text-sm text-hg-text font-sans mt-1 truncate">{nl.subject}</p>
            </div>
            <button
              onClick={() => {
                // Legacy records store draft_json as {sections:[]} only (no subject/
                // preview). Fall back to the always-present top-level fields so the
                // detail view never renders blank.
                let parsed = {};
                try { parsed = JSON.parse(nl.draft_json) || {}; } catch { parsed = {}; }
                setViewDraft({
                  subject: parsed.subject || nl.subject || '',
                  preview: parsed.preview || nl.topic_brief || '',
                  hero_image: parsed.hero_image || nl.hero_image || null,
                  sections: parsed.sections || [],
                });
              }}
              className="text-xs text-hg-text-dim hover:text-hg-green font-sans flex items-center gap-1 transition-colors ml-4 flex-shrink-0"
            >
              <Eye size={12} /> View
            </button>
          </div>
        );
      })}
      {viewDraft && <PreviewModal draft={viewDraft} onClose={() => setViewDraft(null)} />}
    </div>
  );
}

export default function NewsletterStudio({ onNavigate }) {
  const [topicBrief, setTopicBrief] = useState('');
  const [companyUpdate, setCompanyUpdate] = useState('');
  const [issueType, setIssueType] = useState('regular');
  const [audience, setAudience] = useState('fitters');
  const [storyShape, setStoryShape] = useState('auto');
  const [issueDate, setIssueDate] = useState(() => new Date().toISOString().slice(0, 10)); // YYYY-MM-DD, defaults today; drives Content Calendar visibility
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('composer');
  const [showPreview, setShowPreview] = useState(false);
  const [showFormatted, setShowFormatted] = useState(false);
  const [notification, setNotification] = useState('');
  const [pushing, setPushing] = useState(false);

  const showNotif = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(''), 3000);
  };

  const handleGenerate = async () => {
    if (!topicBrief.trim()) return;
    setLoading(true);
    setError('');
    setDraft(null);
    try {
      const res = await axios.post(`${API}/newsletter/generate`, {
        topic_brief: topicBrief,
        company_update: companyUpdate,
        issue_type: issueType,
        audience,
        story_shape: storyShape,
      });
      setDraft(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate newsletter.');
    }
    setLoading(false);
  };

  const handleSectionChange = (idx, newCopy) => {
    if (!draft) return;
    const updated = { ...draft, sections: draft.sections.map((s, i) => i === idx ? { ...s, copy: newCopy } : s) };
    setDraft(updated);
  };

  const handleSectionImageChange = (idx, imageData) => {
    if (!draft) return;
    const updated = { ...draft, sections: draft.sections.map((s, i) => i === idx ? { ...s, image: imageData } : s) };
    setDraft(updated);
  };

  const handleHeroImageChange = (imageData) => {
    if (!draft) return;
    setDraft({ ...draft, hero_image: imageData });
  };

  const handleSave = async () => {
    if (!draft) return;
    try {
      await axios.post(`${API}/newsletter/save`, {
        subject: draft.subject,
        topic_brief: topicBrief,
        company_update: companyUpdate,
        draft_json: JSON.stringify(draft),
        issue_date: issueDate,
        hero_image: draft.hero_image || null,
      });
      showNotif('Newsletter draft saved');
    } catch (e) {
      console.error('Save failed', e);
    }
  };

  const handlePushMailerlite = async () => {
    if (!draft) return;
    setPushing(true);
    setError('');
    try {
      const saveRes = await axios.post(`${API}/newsletter/save`, {
        subject: draft.subject,
        topic_brief: topicBrief,
        company_update: companyUpdate,
        draft_json: JSON.stringify(draft),
        issue_date: issueDate,
        hero_image: draft.hero_image || null,
      });
      const nlId = saveRes.data.newsletter?.id;
      if (nlId) {
        const pushRes = await axios.post(`${API}/newsletter/push-mailerlite?newsletter_id=${nlId}`);
        const data = pushRes.data;
        if (data.mailerlite_url) {
          showNotif('Campaign created in Mailerlite! Opening...');
          setTimeout(() => window.open(data.mailerlite_url, '_blank'), 1000);
        } else {
          showNotif(data.message || 'Newsletter pushed to Mailerlite');
        }
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to push to Mailerlite';
      setError(detail);
    }
    setPushing(false);
  };

  return (
    <div className="space-y-6">
      {notification && (
        <div className="fixed top-4 right-4 z-50 bg-hg-surface border border-hg-green/30 rounded-sm px-4 py-3 text-sm text-hg-green font-sans flex items-center gap-2 animate-fade-in">
          <Check size={14} /> {notification}
        </div>
      )}

      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">NEWSLETTER STUDIO</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">Draft, edit & publish the Teacher to Teacher newsletter.</p>
      </div>

      <div className="flex gap-2 border-b border-hg-border">
        <button
          data-testid="nl-tab-composer"
          onClick={() => setActiveTab('composer')}
          className={`px-4 py-2.5 text-sm font-sans transition-colors border-b-2 -mb-px ${
            activeTab === 'composer' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >
          Composer
        </button>
        {/* "Quick Draft (legacy)" tab retired 2026-07-27 — its render branch below is
            kept (archived, unreachable) per the no-delete principle. */}
        <button
          data-testid="nl-tab-history"
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2.5 text-sm font-sans transition-colors border-b-2 -mb-px ${
            activeTab === 'history' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >
          History
        </button>
        <button
          data-testid="nl-tab-templates"
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2.5 text-sm font-sans transition-colors border-b-2 -mb-px ${
            activeTab === 'templates' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >
          HG Templates
        </button>
        <button
          data-testid="nl-tab-archive"
          onClick={() => setActiveTab('archive')}
          className={`px-4 py-2.5 text-sm font-sans transition-colors border-b-2 -mb-px ${
            activeTab === 'archive' ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
          }`}
        >
          Newsletter Archive
        </button>
      </div>

      {activeTab === 'composer' ? (
        <NewsletterComposer onNavigate={onNavigate} />
      ) : activeTab === 'generate' ? (
        <div className="space-y-6">
          <div className="bg-hg-surface border border-hg-border rounded-sm p-6 space-y-5">
            <div>
              <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Topic Brief</label>
              <textarea
                data-testid="nl-topic-input"
                value={topicBrief}
                onChange={(e) => setTopicBrief(e.target.value)}
                placeholder="What's happening in golf instruction and fitting this issue..."
                className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">From the HG Desk — what's new this issue?</label>
              <textarea
                data-testid="nl-company-input"
                value={companyUpdate}
                onChange={(e) => setCompanyUpdate(e.target.value)}
                placeholder="Product launches, events, announcements..."
                className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Issue Type</label>
              <div className="flex gap-2">
                {['regular', 'special'].map((t) => (
                  <button
                    key={t}
                    data-testid={`nl-type-${t}`}
                    onClick={() => setIssueType(t)}
                    className={`px-4 py-2 text-sm font-sans rounded-sm border transition-colors ${
                      issueType === t ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
                    }`}
                  >
                    {t === 'regular' ? 'Regular Issue' : 'Special Announcement'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Issue Date</label>
              <input
                type="date"
                data-testid="nl-issue-date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className="w-full sm:w-auto bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
              />
              <p className="text-[11px] text-hg-text-dim font-sans mt-1.5">Shows the issue on the Content Calendar.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Audience</label>
                <select
                  data-testid="nl-audience-select"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                >
                  <option value="fitters">Fitters & Teaching Pros (T2T)</option>
                  <option value="golfers">Golfers (Customer Newsletter)</option>
                  <option value="trade">Trade & Industry</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Story shape</label>
                <select
                  data-testid="nl-story-shape-select"
                  value={storyShape}
                  onChange={(e) => setStoryShape(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                >
                  <option value="auto">Auto (pick best fit)</option>
                  <option value="fitting_moment">Fitting Moment</option>
                  <option value="coach_spotlight">Coach Spotlight</option>
                  <option value="component_story">Component Story</option>
                  <option value="industry_take">Industry Take</option>
                  <option value="junior_story">Junior Story</option>
                  <option value="built_in_idaho">Built-in-Idaho</option>
                </select>
              </div>
            </div>
            <button
              data-testid="generate-newsletter-button"
              onClick={handleGenerate}
              disabled={loading || !topicBrief.trim()}
              className="flex items-center gap-2 bg-hg-green text-hg-text font-sans font-medium text-sm px-5 py-2.5 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {loading ? 'Generating...' : 'Generate Draft'}
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-sm text-red-400 font-sans flex items-center gap-2">
              <X size={14} /> {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12 gap-3">
              <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse-green" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse-green" style={{ animationDelay: '300ms' }} />
              <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse-green" style={{ animationDelay: '600ms' }} />
            </div>
          )}

          {draft && (
            <div className="space-y-4">
              <div className="bg-hg-surface border border-hg-border rounded-sm p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-xl text-hg-text tracking-wider">{draft.subject}</h3>
                    <p className="text-xs text-hg-text-dim font-sans mt-1">{draft.preview}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid="nl-preview-btn"
                      onClick={() => setShowPreview(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green transition-colors"
                    >
                      <Eye size={12} strokeWidth={1.5} /> Preview
                    </button>
                    <button
                      data-testid="nl-formatted-preview-btn"
                      onClick={() => setShowFormatted(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green/30 bg-hg-green/5 rounded-sm text-hg-green hover:bg-hg-green/10 transition-colors"
                    >
                      <Mail size={12} strokeWidth={1.5} /> Preview Formatted
                    </button>
                    <button
                      data-testid="nl-save-btn"
                      onClick={handleSave}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green transition-colors"
                    >
                      <FileText size={12} strokeWidth={1.5} /> Save Draft
                    </button>
                    <button
                      data-testid="nl-push-mailerlite-btn"
                      onClick={handlePushMailerlite}
                      disabled={pushing}
                      className="flex items-center gap-2 bg-hg-green text-hg-text text-xs font-sans font-medium px-4 py-1.5 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
                    >
                      {pushing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Push to Mailerlite
                    </button>
                  </div>
                </div>

                <div className="border-t border-hg-border pt-4">
                  <EmailImagePanel
                    testIdPrefix="nl-hero-image"
                    imageData={draft.hero_image}
                    onImageSet={handleHeroImageChange}
                    context="hero"
                    promptContext={draft.subject || 'Henry-Griffitts newsletter hero'}
                    label="Hero Image (top of newsletter)"
                  />
                </div>

                <div className="space-y-3">
                  {draft.sections?.map((section, i) => (
                    <SectionEditor key={i} section={section} index={i} onChange={handleSectionChange} onImageChange={handleSectionImageChange} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {showPreview && <PreviewModal draft={draft} onClose={() => setShowPreview(false)} />}
          {showFormatted && <FormattedPreviewModal draft={draft} onClose={() => setShowFormatted(false)} />}
        </div>
      ) : activeTab === 'history' ? (
        <HistoryTab />
      ) : activeTab === 'templates' ? (
        <TemplateGallery />
      ) : (
        <ArchiveBrowser />
      )}
    </div>
  );
}
